import { getAllCompaniesForGlobalSelection, getUserCompaniesAndRoles } from '../redux/AuthCRUD';
import { parseUserCompaniesAndRolesPayload } from '@/__core/permissions/utils/permissionResolver';
import { CompanyData, SelectedCompany, saveSelectedCompany } from '../helpers/CompanyStorage';
import * as auth from '../redux/AuthRedux';
import { getPathWithBasename } from '@/__core/helpers/getBasename';
import { clearUserSession } from './clearUserSession';

/** After switching company, land on /main — current route may be forbidden in the new company. */
export const redirectAfterCompanySwitch = (): void => {
  window.location.replace(getPathWithBasename('/main'));
};

export const clearAuthTokens = () => {
  localStorage.removeItem('rememberToken');
  localStorage.removeItem('rememberUser');
  sessionStorage.removeItem('sessionToken');
  sessionStorage.removeItem('sessionUser');
  localStorage.removeItem('access_token');
  sessionStorage.removeItem('access_token');
  localStorage.removeItem('persist:v100-xbox-auth');
  sessionStorage.removeItem('persist:v100-xbox-auth');
};

export const logout = async () => {
  await clearUserSession();
  window.location.href = getPathWithBasename('/auth/login');
};

/** Clear session and redirect to login (e.g. after password change). */
export const logoutAndRedirectToLogin = async (
  dispatch: (action: ReturnType<typeof auth.actions.logout>) => void
): Promise<void> => {
  dispatch(auth.actions.logout());
  await clearUserSession();
  window.location.replace(getPathWithBasename('/auth/login'));
};

/**
 * AUTHENTICATION RULES - QUY LUẬT XÁC THỰC
 * 
 * Đây là bộ quy luật QUAN TRỌNG cho việc xử lý authentication trong hệ thống.
 * KHÔNG ĐƯỢC sửa đổi các quy luật này mà không thảo luận và đồng ý trước.
 * 
 * QUY LUẬT 1: Global Role Users
 * - Nếu user có role global (has_global_role = true):
 *   + BẮT BUỘC chọn company để làm việc
 *   + Có thể chọn bất kỳ company nào trong danh sách API trả về
 *   + Lưu global roles vào Redux state
 * 
 * QUY LUẬT 2: Single Company Users
 * - Nếu user chỉ có 1 công ty (companies.length === 1):
 *   + Tự động chọn công ty đó
 *   + Lưu thông tin công ty vào storage
 *   + Redirect đến /main
 * 
 * QUY LUẬT 3: Multiple Companies Users
 * - Nếu user có 2+ công ty thuộc các companies khác nhau:
 *   + BẮT BUỘC phải hiển thị modal chọn công ty
 *   + Chỉ khi user chọn công ty mới redirect đến /main
 *   + Lưu thông tin công ty đã chọn vào storage
 * 
 * QUY LUẬT 4: Empty Company List
 * - Nếu user không có company nào trong danh sách:
 *   + Cho phép vào /main ngay
 *   + Không hiển thị modal chọn company
 * 
 * QUY LUẬT 5: Company Storage
 * - Thông tin công ty PHẢI được lưu vào localStorage/sessionStorage
 * - Sử dụng hàm saveSelectedCompany với remember flag tương ứng
 * - selectedCompany trong Redux phải sync với storage
 * 
 * QUY LUẬT 6: Auto-login Prevention
 * - Khi user đã authenticated và có selectedCompany:
 *   + Các trang auth (login, forgot-password) PHẢI redirect về /main
 *   + Không được hiển thị lại form login
 */

interface CompanySelectionResult {
  type: 'single' | 'multiple' | 'none';
  companies?: CompanyData[];
  selectedCompany?: SelectedCompany;
  globalRoles?: any[];
  isGlobalUser?: boolean;
}

export const normalizeCompaniesById = (companies: any[]): CompanyData[] => {
  const companyMap = new Map<string, CompanyData>();

  companies.forEach((company: any) => {
    const normalizedCompanyId = company?.company_id || company?.id;
    const key = String(normalizedCompanyId || '');
    if (!key) return;

    const existing = companyMap.get(key);
    if (!existing) {
      companyMap.set(key, {
        ...company,
        company_id: String(normalizedCompanyId),
        company_name: company?.company_name || company?.name || '',
        company_code: company?.company_code || company?.code || '',
        company_type: company?.company_type || 'company',
        roles: Array.isArray(company.roles) ? company.roles : []
      });
      return;
    }

    const mergedRolesRaw = [
      ...(Array.isArray(existing.roles) ? existing.roles : []),
      ...(Array.isArray(company.roles) ? company.roles : [])
    ];

    const roleMap = new Map<string, any>();
    mergedRolesRaw.forEach((role: any) => {
      const roleKey = String(role?.role_id || `${role?.role_name || 'unknown'}:${role?.role_level || 'company'}`);
      if (!roleMap.has(roleKey)) {
        roleMap.set(roleKey, role);
      }
    });

    companyMap.set(key, {
      ...existing,
      company_id: String(normalizedCompanyId),
      company_name: existing.company_name || company?.company_name || company?.name || '',
      company_code: existing.company_code || company?.company_code || company?.code || '',
      company_type: existing.company_type || company?.company_type || 'company',
      roles: Array.from(roleMap.values())
    });
  });

  return Array.from(companyMap.values());
};

/**
 * Xử lý việc kiểm tra và chọn công ty theo các quy luật authentication
 * @param dispatch - Redux dispatch function
 * @param remember - Có lưu vào localStorage hay không
 * @returns Kết quả xử lý company selection
 */
/** Load companies/roles into Redux (e.g. after refresh when only selectedCompany was restored). */
export const hydrateAuthCompanies = async (dispatch: any): Promise<CompanyData[]> => {
  const response = await getUserCompaniesAndRoles();
  const parsed = parseUserCompaniesAndRolesPayload(response.data);

  let companiesData: CompanyData[] = parsed.companies;
  const globalRoles = parsed.globalRoles;
  const isGlobalUser = parsed.hasGlobalRole;

  dispatch(auth.actions.setGlobalRoles(globalRoles));
  dispatch(auth.actions.setHasGlobalRole(isGlobalUser));

  if (isGlobalUser && companiesData.length === 0) {
    try {
      const allCompanies = await getAllCompaniesForGlobalSelection();
      companiesData = allCompanies.map((company: any) => ({
        company_id: String(company.id),
        company_name: company.name,
        company_code: company.code,
        company_type: company.company_type,
        roles: [],
      }));
    } catch (error) {
      console.error('Error fetching all companies for global selection:', error);
    }
  }

  dispatch(auth.actions.setCompanies(companiesData));
  return companiesData;
};

export const processCompanySelection = async (
  dispatch: any,
  remember: boolean = false
): Promise<CompanySelectionResult> => {
  try {
    // Gọi API để lấy thông tin companies và roles
    const response = await getUserCompaniesAndRoles();
    const parsed = parseUserCompaniesAndRolesPayload(response.data);

    let companiesData: CompanyData[] = parsed.companies;
    const globalRoles = parsed.globalRoles;
    const isGlobalUser = parsed.hasGlobalRole;

    dispatch(auth.actions.setGlobalRoles(globalRoles));
    dispatch(auth.actions.setHasGlobalRole(isGlobalUser));

    // For global users, fallback to full company list if company-specific roles are empty.
    if (isGlobalUser && companiesData.length === 0) {
      try {
        const allCompanies = await getAllCompaniesForGlobalSelection();
        companiesData = allCompanies.map((company: any) => ({
          company_id: company.id,
          company_name: company.name,
          company_code: company.code,
          company_type: company.company_type,
          roles: []
        }));
      } catch (error) {
        console.error('Error fetching all companies for global selection:', error);
      }
    }

    // QUY LUẬT 4: Empty Company List
    if (companiesData.length === 0) {
      const selectedCompany: SelectedCompany = {
        company_id: '',
        company_name: 'No Company',
        company_code: '',
        company_type: 'none',
        roles: [],
        selected_at: new Date().toISOString()
      };

      dispatch(auth.actions.setCompanies([]));
      dispatch(auth.actions.setSelectedCompany(selectedCompany));

      return {
        type: 'none',
        companies: [],
        selectedCompany,
        globalRoles,
        isGlobalUser
      };
    }
    
    // QUY LUẬT 2: Single Company
    if (!isGlobalUser && companiesData.length === 1) {
      const company = companiesData[0];
      
      // Lưu công ty vào storage
      saveSelectedCompany(company, remember);
      
      // Dispatch selected company
      const selectedCompany: SelectedCompany = {
        company_id: company.company_id,
        company_name: company.company_name,
        company_code: company.company_code,
        company_type: company.company_type,
        roles: company.roles.map((role: any) => ({
          role_id: role.role_id,
          role_name: role.role_name,
          role_display_name: role.role_display_name,
          role_level: role.role_level || 'company',
          permissions: role.permissions || {},
          is_primary: role.is_primary,
          created_at: role.created_at
        })),
        selected_at: new Date().toISOString()
      };
      dispatch(auth.actions.setSelectedCompany(selectedCompany));
      dispatch(auth.actions.setCompanies(companiesData));

      return {
        type: 'single',
        companies: companiesData,
        selectedCompany,
        globalRoles,
        isGlobalUser
      };
    }

    // QUY LUẬT 1 + 3: Global users hoặc users có nhiều company đều phải chọn company
    dispatch(auth.actions.setCompanies(companiesData));

    return {
      type: 'multiple',
      companies: companiesData,
      globalRoles,
      isGlobalUser
    };
    
  } catch (error) {
    console.error('Error processing company selection:', error);
    throw error;
  }
}; 